import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';

export type GiftCardStatus = 'pending_fulfillment' | 'fulfilled' | 'refunded';

export interface GiftCardRedemption {
  id: string;
  /** Snapshot from order time — never the brand's current name. */
  brand_name: string;
  face_value_cents: number;
  coins_cost: number;
  delivery_email: string;
  status: GiftCardStatus;
  created_at: string;
  failure_reason: string | null;
}

export interface GiftCardTier {
  id: string;
  face_value_cents: number;
  coins_cost: number;
  /** Remaining commitment count, shared across every brand in the tier. */
  stock_remaining: number;
  is_active: boolean;
}

interface QueuePayload {
  redemptions: GiftCardRedemption[];
  tiers: GiftCardTier[];
}

/**
 * The gift card fulfilment queue. Nothing is pre-purchased, so this is a
 * shopping list: each row names the SKU to buy and the address to send it to,
 * oldest first — which is both the work order and the first-come promise.
 */
export function useGiftCardQueue(env: Env, status: GiftCardStatus) {
  const { request } = useAdminApi();
  const [data, setData] = useState<QueuePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<QueuePayload>({
          env,
          path: '/admin/gamification/gift-cards/redemptions',
          query: { status },
        });
        if (mounted) setData(payload);
      } catch (e) {
        if (mounted) {
          setError(e instanceof ApiError ? e.message : 'Failed to load the gift card queue');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [env, status, tick, request]);

  return { data, loading, error, refetch };
}

/**
 * Fulfil and refund.
 *
 * Fulfil sends the code and only then flips the row — a failed send returns 502
 * and leaves the row pending with a failure_reason, so it stays in the queue.
 * That is why a 502 here is reported as "not sent, still queued" rather than as
 * a generic error: the distinction is the whole point of the ordering.
 *
 * SES is in sandbox today, so sending to a personal address fails by design
 * until production access lands.
 */
export function useGiftCardActions(env: Env) {
  const { request } = useAdminApi();
  const [busyId, setBusyId] = useState<string | null>(null);

  const fulfil = useCallback(
    async (id: string, code: string) => {
      setBusyId(id);
      try {
        await request({
          env,
          path: `/admin/gamification/gift-cards/redemptions/${id}/fulfil`,
          method: 'POST',
          body: { code },
        });
        return { ok: true as const };
      } catch (e) {
        const message = e instanceof ApiError ? e.message : 'Fulfil failed';
        const sendFailed = e instanceof ApiError && e.status === 502;
        return { ok: false as const, message, sendFailed };
      } finally {
        setBusyId(null);
      }
    },
    [env, request],
  );

  const refund = useCallback(
    async (id: string, reason: string) => {
      setBusyId(id);
      try {
        await request({
          env,
          path: `/admin/gamification/gift-cards/redemptions/${id}/refund`,
          method: 'POST',
          body: { reason },
        });
        return { ok: true as const };
      } catch (e) {
        return {
          ok: false as const,
          message: e instanceof ApiError ? e.message : 'Refund failed',
        };
      } finally {
        setBusyId(null);
      }
    },
    [env, request],
  );

  return { fulfil, refund, busyId };
}
